import React from 'react';

import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useSetRecoilState } from 'recoil';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import { registerUser } from 'modules/user/api/user';
import { userInsertSelector } from 'modules/user/state/user-insert';
import { useMarketingProfileAsUser } from 'modules/marketing-profile/hooks/useMarketingProfileAsUser';
import { getAxiosErrorOrThrow } from 'shared/utils/error';
import { updateProfile } from 'modules/marketing-profile/api';
import { useGuaranteedAuth } from 'shared/hooks/useAuth';
import { IUpdateMarketingProfile } from 'modules/marketing-profile/models/marketing-profile';
import { Config } from 'shared/config';

import { FormError } from 'shared/components/forms/FormError/FormError';


const studentRegistrationExtraInfoSchema = Yup.object().shape({
    accept_terms: Yup.boolean().required().isTrue(),
});

export const ParentRegistrationForm = () => {
    const {t} = useTranslation();
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

    const {tokenData, tenantId} = useGuaranteedAuth();

    const profile = useMarketingProfileAsUser(tenantId, tokenData.id);

    const insertUser = useSetRecoilState(userInsertSelector);
    
    const {handleSubmit, formState: {errors}, register} = useForm<IUpdateMarketingProfile>({
        resolver: yupResolver(studentRegistrationExtraInfoSchema),
        defaultValues: {
            accept_terms: undefined,
        },
    });

    const onSubmit = (data: IUpdateMarketingProfile) => {
        if (!profile?.id || isSubmitting) {
            return;
        }
        if (!data.accept_terms) {
            toast.error(t(
                'userInfoForm.form.registerError',
                'You must accept the terms and conditions to use the platform',
            ));
        }
        setIsSubmitting(true);
        (async () => {
            try {
                await updateProfile(tenantId, profile.id, data);
                const updatedUser = await registerUser(tenantId, tokenData.id);
                insertUser(updatedUser);
            } catch (e) {
                toast.error(getAxiosErrorOrThrow(e));
            } finally {
                setIsSubmitting(false);
            }
        })();
    };
    
    return (
        <Container fluid className="d-flex vh-100">
            <Row className="w-100">
                <Col xs={12} md={6} lg={6} className="mx-auto">
                    <h1 style={{textAlign: 'center', marginTop: '20px', marginBottom: '20px'}}>
                        {t('userInfoForm.form.title', 'User Registration Form')}
                    </h1>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <Form.Group controlId="accept_terms" className="mb-1 d-flex align-items-center">
                            <Form.Check
                                className="form-control-sm align-bottom"
                                type="checkbox"
                                label={t(
                                    'userRegistrationInfo.form.accept_terms',
                                    'I have read the terms and conditions on the Young Eyes website.',
                                )}
                                style={{fontSize: '1rem'}}
                                {...register('accept_terms')}
                            />
                            <a href={Config.TCLinkUrl} target="_blank" style={{fontSize: '1rem'}} rel="noreferrer">
                                {t('userRegistrationInfo.form.link', 'See here')}
                            </a>
                        </Form.Group>
                        <FormError name="accept_terms" errors={errors} />
                        <div className="d-flex mt-4 align-items-center">
                            <Button
                                className='m-0'
                                variant="primary"
                                type="submit"
                                size="lg"
                                style={{margin: '0 auto', display: 'block', fontSize: 'larger'}}
                                disabled={isSubmitting}
                            >
                                {t('common.submit', 'Submit')}
                            </Button>
                        </div>
                    </Form>
                </Col>
            </Row>
        </Container>
    );
};